import { Alert, Descriptions, Flex, Modal, Space, Tag, Typography, theme } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { TableSkeleton } from '@/shared/ui/table/TableSkeleton'
import { fetchLedgerTxn } from './ledgerApi'
import { formatLedgerAmount } from './ledgerAmount'

interface LedgerTxnModalProps {
  /** `null` — oyna yopiq */
  txnId: string | null
  onClose: () => void
}

/**
 * Bitta tranzaksiyaning IKKI TOMONI.
 *
 * <h2>⚠️ NEGA BU OYNA KERAK BO'LDI</h2>
 * Hisob ko'chirmasi FAQAT bitta hisobning satrlarini ko'rsatadi, ya'ni
 * «pul qayerdan keldi» degan savolga javob bermaydi — ikkinchi satr
 * boshqa hisobda. `GET /api/ledger/txns/{id}` esa tranzaksiyaning
 * BARCHA satrlarini beradi. Endpoint backendda bor edi, panel esa unga
 * UMUMAN murojaat qilmasdi (`fetchLedgerTxn` hech qayerdan
 * chaqirilmagan edi).
 *
 * <h2>⚠️ JADVAL EMAS, RO'YXAT — ATAYLAB</h2>
 * Ikki tomonlama yozuvda satrlar IKKITADAN boshlanadi va o'nlab
 * bo'lmaydi. Bunga jadval qo'yish sahifalash tugmalarini ham olib
 * kelardi, `GET /api/ledger/txns/{id}` da esa `page`/`size` YO'Q —
 * ya'ni tugmalar bezak bo'lardi.
 *
 * <h2>⚠️ `replay` KO'RSATILADI</h2>
 * `true` — bu tranzaksiya idempotentlik kaliti bo'yicha QAYTA
 * qaytarilgan, ya'ni yangi yozuv YOZILMAGAN. Uni yashirish «ikki marta
 * yozildi» degan xato xulosaga yo'l ochardi.
 */
export function LedgerTxnModal({ txnId, onClose }: LedgerTxnModalProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const txn = useQuery({
    queryKey: queryKeys.ledger.txn(txnId ?? 'none'),
    queryFn: ({ signal }) => fetchLedgerTxn(txnId ?? '', signal),
    enabled: txnId !== null,
  })

  return (
    <Modal
      open={txnId !== null}
      title={t('ledger.txn.title')}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnHidden
    >
      {/*
        ⚠️ TO'RT HOLAT AJRATILGAN: yuklanmoqda (skeleton), xato (SABABI
        bilan), satrsiz javob va ma'lumot bor. Ularni bitta spinner
        ostiga yig'ish «nega bo'sh» savolini javobsiz qoldirardi.
      */}
      {txn.isPending ? (
        <TableSkeleton />
      ) : txn.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('ledger.txn.loadFailed')}
          description={translateError(txn.error, t)}
        />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {txn.data.replay ? <Alert type="info" showIcon message={t('ledger.txn.replay')} /> : null}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('audit.columns.at')}>
              <span title={utcTooltip(txn.data.createdAt)}>
                {formatDateTime(txn.data.createdAt)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('ledger.txn.kind')}>
              {txn.data.kind ? (
                <Tag>{t(`txnKind.${txn.data.kind}`, { defaultValue: txn.data.kind })}</Tag>
              ) : (
                t('common.dash')
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('ledger.adjust.reason')}>
              {txn.data.note ?? t('common.dash')}
            </Descriptions.Item>
            <Descriptions.Item label={t('ledger.adjust.reference')}>
              {txn.data.reference ?? t('common.dash')}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Typography.Text strong>{t('ledger.txn.lines')}</Typography.Text>
            {txn.data.entries.length === 0 ? (
              <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('ledger.txn.noLines')}
              </Typography.Paragraph>
            ) : (
              <Flex vertical style={{ marginTop: 8 }}>
                {txn.data.entries.map((line) => (
                  <Flex
                    key={line.id}
                    align="center"
                    justify="space-between"
                    gap="small"
                    style={{
                      padding: '8px 12px',
                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    }}
                  >
                    <Typography.Text
                      code
                      copyable={{ text: line.accountId }}
                      style={{ fontSize: 12 }}
                    >
                      {shortId(line.accountId)}
                    </Typography.Text>
                    {/* ⚠️ Ishora KELISHUVI `ledger` niki: musbat = kredit,
                        manfiy = debit (`docs/02-LEDGER.md` §7). Panel uni
                        AG'DARMAYDI — ag'darish bir kuni bir joyda unutilib,
                        daromadni xarajat qilib ko'rsatardi. */}
                    <Typography.Text
                      type={line.amount < 0 ? 'danger' : 'success'}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {line.amount >= 0 ? '+' : ''}
                      {formatLedgerAmount(line.amount, line.unit)}
                    </Typography.Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </div>
        </Space>
      )}
    </Modal>
  )
}
